import { Routes, Route, Navigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { roleCanManage, roleCanBrowseClients } from '@shared/enums'
import { useSession } from './hooks/useAuth'
import { getSettings } from './lib/api'
import { AppShell } from './components/shell/AppShell'
import { AccountNotLinked } from './routes/AccountNotLinked'
import { Login } from './routes/Login'
import { ForgotPassword } from './routes/ForgotPassword'
import { ResetPassword } from './routes/ResetPassword'
import { Today } from './routes/Today'
import { Clients } from './routes/Clients'
import { ClientDetail } from './routes/ClientDetail'
import { Tasks } from './routes/Tasks'
import { Team } from './routes/Team'

export default function App() {
  const { data: session, isLoading, isError } = useSession()

  // Settings is the cheapest authed endpoint and every screen needs it anyway
  // (disciplines), so it doubles as the probe for whether this session is
  // usable at all — `/api/auth/*` skips the auth middleware, so a valid
  // session tells us nothing about the employee link behind it.
  const probe = useQuery({
    queryKey: ['settings'],
    queryFn: getSettings,
    enabled: !!session?.user,
    retry: false,
    staleTime: 5 * 60_000,
  })
  const probeCode = (probe.error as { body?: { error?: { code?: string } } } | null)?.body?.error?.code

  // Loading state
  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
      </div>
    )
  }

  // Not authenticated — show login + password-reset flow
  if (!session?.user || isError) {
    return (
      <Routes>
        <Route path="forgot-password" element={<ForgotPassword />} />
        <Route path="reset-password" element={<ResetPassword />} />
        <Route path="*" element={<Login />} />
      </Routes>
    )
  }

  // Signed in, but the API won't serve this session — say so plainly instead
  // of rendering a shell where every panel errors.
  if (probeCode === 'account_not_linked' || probeCode === 'account_deactivated') {
    return <AccountNotLinked reason={probeCode === 'account_deactivated' ? 'deactivated' : 'not_linked'} />
  }

  // Team is account administration — the roster carries everyone's email,
  // platform role and login state — so it stays behind admin. Client pages are
  // working hubs for the Team role too (LXP-55); the API strips financials and
  // keeps contracts and client edits admin-only. Unregistering a route keeps a
  // bookmarked URL from rendering a page with nothing usable in it.
  const role = (session.user as { role?: string }).role ?? 'member'
  const canManage = roleCanManage(role)
  const canBrowseClients = roleCanBrowseClients(role)

  // Authenticated — show app
  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route index element={<Today />} />
        {canBrowseClients && <Route path="clients" element={<Clients />} />}
        {canBrowseClients && <Route path="clients/:id" element={<ClientDetail />} />}
        <Route path="tasks" element={<Tasks />} />
        {canManage && <Route path="team" element={<Team />} />}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  )
}
